import { Component, ElementRef, OnDestroy, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { TranslateService } from '@ngx-translate/core';
import { BehaviorSubject, Subscription } from 'rxjs';
import { ConfirmDialogService } from '../confirm-dialog.service';
import { EstensaUser } from '../../models/vnext.models';
import { AuthenticationService } from '../../services/authentication/authentication.service';
import { ModalDialogNotificationService, NotificationService, NotificationType } from '../../services/notification.service';
import { AuthData } from '@se/models';

@Component({
    selector: 'se-confirm-dialog',
    templateUrl: './confirm-dialog.component.html',
    styleUrls: ['./confirm-dialog.component.scss'],
    standalone: false
})
export class ConfirmDialogComponent implements OnDestroy, OnInit {
    @ViewChild('confirmDialogTemplate')
    confirmDialogTemplate!: TemplateRef<any>;

    @ViewChild('pwd')
    pwdElement!: ElementRef;

    get isWaiting(): BehaviorSubject<boolean> {
        return this.confirmDialogService.isWaiting;
    }

    confirmDialog!: MatDialogRef<any>;

    isOnError: boolean = false;
    errorMessage: string = '';

    // data members
    data: AuthData = {
        userName: '',
        password: '',
        domain: '',
    };

    showPassword: boolean = false;

    private afterCloseEventSubscription!: Subscription;
    private notifyFailureSubscription: Subscription;
    private notifySuccessSubscription: Subscription;
    private openSubscription: Subscription;

    // constructor

    constructor(
        public dialog: MatDialog,
        private confirmDialogService: ConfirmDialogService,
        private translateService: TranslateService,
        private notificationService: NotificationService,
        private authenticationService: AuthenticationService,
        private modalNotificationService: ModalDialogNotificationService,
    ) {
        this.openSubscription = this.confirmDialogService.openConfirmWindowRequired.subscribe((userId?: string) => {
            this.authenticationService.getUserInfo(userId).then((estensaUser: EstensaUser) => {
                this.showDialog(estensaUser);
            });
        });

        this.notifySuccessSubscription = this.confirmDialogService.notifySuccess.subscribe((result) => {
            this.confirmDialog?.close(true);

            if (!result) {
                this.notificationService.success(
                    this.translateService.instant(this.confirmDialogService.successMessageKey),
                );
                this.modalNotificationService.notify({
                    message: this.translateService.instant(this.confirmDialogService.successMessageKey),
                    title: this.translateService.instant('Information'),
                    type: NotificationType.information,
                    buttonYes: false,
                    buttonNo: false,
                    buttonOk: true,
                    buttonCancel: false,
                });
            } else if (typeof result === 'object') {
                this.modalNotificationService.notify({
                    message: this.translateService.instant(this.confirmDialogService.partialMessageKey, {
                        p0: result.operationFailedCount,
                        p1: result.operationCount,
                    }),
                    title: this.translateService.instant('Warning'),
                    type: NotificationType.warning,
                    buttonYes: false,
                    buttonNo: false,
                    buttonOk: true,
                    buttonCancel: false,
                });
            }
        });

        this.notifyFailureSubscription = this.confirmDialogService.notifyFailure.subscribe((message) => {
            this.isOnError = true;

            const errorTitle = this.translateService.instant(this.confirmDialogService.errorMessageKey, { p0: '' });
            this.errorMessage = errorTitle;

            this.data.password = ''; // Resetto la password
            setTimeout(() => {
                if (this.pwdElement && this.pwdElement.nativeElement) this.pwdElement.nativeElement.focus();
            }, 0);

            this.notificationService.error(!!message && message != errorTitle ? message : '', errorTitle); // Aggiungo il dettaglio al messaggio generico nella notifica
        });
    }

    ngOnInit() {
        this.safeExit();
    }

    ngOnDestroy() {
        this.notifyFailureSubscription.unsubscribe();
        this.notifySuccessSubscription.unsubscribe();
        this.openSubscription.unsubscribe();

        if (this.afterCloseEventSubscription) {
            this.afterCloseEventSubscription.unsubscribe();
        }
    }

    public closeModal(cancelOps: boolean) {
        this.isOnError = false;

        if (!cancelOps) {
            // Richiedo operazione
            this.confirmDialogService.endConfirmFlow(this.data);
        } else {
            // Chiudo
            this.confirmDialogService.cancelConfirmFlow();
            this.confirmDialog.close(true);
        }
    }

    public enableAuthenticateButton = () => {
        return !!this.data && !!this.data.userName && !!this.data.password;
    };

    // private functions

    private showDialog = (estensaUser: EstensaUser) => {
        this.data.userName = estensaUser.userName;
        this.data.domain = estensaUser.domain;

        this.confirmDialog = this.dialog.open(this.confirmDialogTemplate, {
            autoFocus: true,
            height: '35%',
            width: '25%',
            minHeight: '355px',
            minWidth: '480px',
            disableClose: true,
            panelClass: 'outer-sign-dialog-wrapper',
        });

        this.afterCloseEventSubscription = this.confirmDialog.afterClosed().subscribe((wasClosedAutomatically) => {
            // Alla chiusura della dialog faccio le pulizie del caso
            if (!wasClosedAutomatically) {
                this.confirmDialogService.cancelConfirmFlow();
            }
            this.safeExit();
            this.confirmDialogService.safeExit();
        });
    };

    private safeExit = () => {
        this.isOnError = false;
        this.errorMessage = '';
        this.data = {
            userName: '',
            password: '',
            domain: '',
        };

        if (this.afterCloseEventSubscription) {
            this.afterCloseEventSubscription.unsubscribe();
        }
    };
}
